import { createFileRoute } from "@tanstack/react-router";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { useNav } from "@/utils/nav";
import { useReminderSendVolume, type KnockSendVolumeDay } from "@/features/admin/hooks/useReminderSendVolume";
import {
    ChartContainer,
    ChartLegend,
    ChartLegendContent,
    ChartTooltip,
    ChartTooltipContent,
    type ChartConfig,
} from "@patient-watch/ui/chart";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import Spinner from "@/components/common/Spinner";
import dayjs from "dayjs";

export const Route = createFileRoute("/_app/developer/reminder-send-volume")({
    component: ReminderSendVolumePage,
});

// Rolling window used for the dashed baseline line (matches cron-check-knock-send-volume).
const BASELINE_WINDOW_DAYS = 7;

const chartConfig: ChartConfig = {
    "entry-reminders-workflow": { label: "Entry reminders", color: "#4f46e5" },
    "entry-reminders-workflow__baseline": { label: "Entry reminders (7d avg)", color: "#a5b4fc" },
    "new-diary-workflow": { label: "New diary ladder", color: "#0891b2" },
    "new-diary-workflow__baseline": { label: "New diary ladder (7d avg)", color: "#a5f3fc" },
};

type ChartPoint = { day: string } & Record<string, number | string>;

function buildChartData(rows: KnockSendVolumeDay[]): { chartData: ChartPoint[]; workflowKeys: string[] } {
    const days = [...new Set(rows.map((row) => row.day))].sort();
    const workflowKeys = [...new Set(rows.map((row) => row.workflow_key))];

    const byWorkflow = new Map<string, Map<string, number>>();
    for (const key of workflowKeys) {
        byWorkflow.set(
            key,
            new Map(rows.filter((row) => row.workflow_key === key).map((row) => [row.day, row.send_count]))
        );
    }

    const chartData = days.map((day, index) => {
        const point: ChartPoint = { day };

        for (const key of workflowKeys) {
            const seriesByDay = byWorkflow.get(key)!;
            point[key] = seriesByDay.get(day) ?? 0;

            const windowDays = days.slice(Math.max(0, index - BASELINE_WINDOW_DAYS), index);
            if (windowDays.length === BASELINE_WINDOW_DAYS) {
                const windowValues = windowDays.map((d) => seriesByDay.get(d) ?? 0);
                point[`${key}__baseline`] = windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length;
            }
        }

        return point;
    });

    return { chartData, workflowKeys };
}

function ReminderSendVolumePage() {
    useNav([
        { label: "Developer", route: { to: "/developer" } },
        { label: "Reminder send volume", route: { to: "/developer/reminder-send-volume" } },
    ]);

    const { data, isLoading, error } = useReminderSendVolume();
    const { chartData, workflowKeys } = buildChartData(data ?? []);

    return (
        <AdminPageLayout
            title="Reminder Send Volume"
            description="Daily Knock sends for entry-reminders and new-diary workflows vs. a rolling 7-day baseline."
        >
            <div className="space-y-4">
                {isLoading && (
                    <div className="flex justify-center p-8">
                        <Spinner />
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 p-4 rounded-lg text-red-800">
                        Error loading send volume: {error instanceof Error ? error.message : "Unknown error"}
                    </div>
                )}

                {!isLoading && !error && chartData.length === 0 && (
                    <div className="bg-amber-50 p-4 rounded-lg text-amber-800">
                        No data yet. `cron-check-knock-send-volume` writes one row per workflow per day, so a full chart
                        appears once the check has run for a while.
                    </div>
                )}

                {chartData.length > 0 && (
                    <div className="bg-white p-4 border rounded-lg">
                        <ChartContainer config={chartConfig} className="w-full h-[400px] aspect-auto">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                                    <CartesianGrid vertical={false} />
                                    <XAxis
                                        dataKey="day"
                                        tickFormatter={(value) => dayjs(value).format("MMM D")}
                                        minTickGap={24}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <ChartTooltip
                                        content={
                                            <ChartTooltipContent
                                                labelFormatter={(value) => dayjs(value).format("MMM D, YYYY")}
                                            />
                                        }
                                    />
                                    <ChartLegend content={<ChartLegendContent />} />
                                    {workflowKeys.map((key) => (
                                        <Line
                                            key={key}
                                            type="monotone"
                                            dataKey={key}
                                            stroke={`var(--color-${key})`}
                                            strokeWidth={2}
                                            dot={false}
                                        />
                                    ))}
                                    {workflowKeys.map((key) => (
                                        <Line
                                            key={`${key}__baseline`}
                                            type="monotone"
                                            dataKey={`${key}__baseline`}
                                            stroke={`var(--color-${key}__baseline)`}
                                            strokeWidth={1.5}
                                            strokeDasharray="4 4"
                                            dot={false}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </div>
                )}
            </div>
        </AdminPageLayout>
    );
}
